"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
// Header & Footer provided by (marketing)/layout.tsx

/* ═══════════════════════════════════════════════════════════════════════════
   Previous Releases Page — /download/releases
   Shows all previous releases (IDE and Code Agent) with download links.
   Product tab switcher fetches /api/releases (IDE) or ?product=agent.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Types ────────────────────────────────────────────────────── */

interface ManifestAsset {
  url: string;
  sha256: string;
  size: number;
}

interface Release {
  version: string;
  commit: string;
  date: string;
  quality: string;
  productVersion: string;
  assets: Record<string, ManifestAsset>;
}

interface ReleasesResponse {
  releases: Release[];
  total: number;
}

/* ── Helpers ──────────────────────────────────────────────────── */

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Map asset key to human-readable label and icon */
function assetLabel(key: string): { label: string; os: string; icon: string } {
  const map: Record<string, { label: string; os: string; icon: string }> = {
    "darwin-arm64-dmg":       { label: "macOS Apple Silicon (.dmg)",     os: "macOS",   icon: "🍎" },
    "darwin-arm64-archive":   { label: "macOS Apple Silicon (.zip)",     os: "macOS",   icon: "🍎" },
    "darwin-x64-dmg":         { label: "macOS Intel (.dmg)",             os: "macOS",   icon: "🍎" },
    "darwin-x64-archive":     { label: "macOS Intel (.zip)",             os: "macOS",   icon: "🍎" },
    "win32-x64-setup":        { label: "Windows x64 Installer (.exe)",  os: "Windows", icon: "🪟" },
    "win32-x64-archive":      { label: "Windows x64 (.zip)",            os: "Windows", icon: "🪟" },
    "win32-arm64-setup":      { label: "Windows ARM64 Installer (.exe)",os: "Windows", icon: "🪟" },
    "win32-arm64-archive":    { label: "Windows ARM64 (.zip)",           os: "Windows", icon: "🪟" },
    "linux-x64-deb":          { label: "Linux x64 (.deb)",              os: "Linux",   icon: "🐧" },
    "linux-x64-rpm":          { label: "Linux x64 (.rpm)",              os: "Linux",   icon: "🐧" },
    "linux-x64-archive":      { label: "Linux x64 (.tar.gz)",           os: "Linux",   icon: "🐧" },
    "linux-arm64-archive":    { label: "Linux ARM64 (.tar.gz)",         os: "Linux",   icon: "🐧" },
    // Code Agent assets
    "win32-x64-msi":          { label: "Windows x64 (.msi)",            os: "Windows", icon: "🪟" },
    "linux-x64-appimage":     { label: "Linux x64 (.AppImage)",         os: "Linux",   icon: "🐧" },
  };

  return map[key] || { label: key, os: "Other", icon: "📦" };
}

/** Group assets by OS */
function groupAssetsByOS(assets: Record<string, ManifestAsset>): Record<string, { key: string; asset: ManifestAsset; meta: ReturnType<typeof assetLabel> }[]> {
  const groups: Record<string, { key: string; asset: ManifestAsset; meta: ReturnType<typeof assetLabel> }[]> = {};

  for (const [key, asset] of Object.entries(assets)) {
    const meta = assetLabel(key);
    if (!groups[meta.os]) groups[meta.os] = [];
    groups[meta.os].push({ key, asset, meta });
  }

  return groups;
}

/* ── Components ──────────────────────────────────────────────── */

function ReleaseCard({ release, isLatest }: { release: Release; isLatest: boolean }) {
  const [expanded, setExpanded] = useState(isLatest);
  const grouped = groupAssetsByOS(release.assets);
  const osOrder = ["macOS", "Windows", "Linux"];

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${isLatest ? "rgba(16,185,129,0.2)" : "var(--border)"}`,
        boxShadow: isLatest ? "0 0 0 1px rgba(16,185,129,0.08), 0 2px 8px rgba(0,0,0,0.04)" : "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-7 py-5 text-left transition-colors hover:bg-[rgba(15,23,42,0.02)]"
      >
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-[16px] font-bold text-foreground font-mono">
                v{release.version}
              </h3>
              {isLatest && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full"
                  style={{
                    background: "rgba(16,185,129,0.08)",
                    color: "var(--success)",
                    border: "1px solid rgba(16,185,129,0.15)",
                  }}
                >
                  Latest
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-[12px] text-(--text-muted)">
              <span>{formatDate(release.date)}</span>
              <span className="font-mono opacity-60">
                {release.commit?.substring(0, 8)}
              </span>
              <span>{Object.keys(release.assets).length} assets</span>
            </div>
          </div>
        </div>

        <svg
          className="w-4 h-4 text-(--text-muted) transition-transform duration-300"
          style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Expanded assets */}
      {expanded && (
        <div className="px-7 pb-6 pt-2">
          <div className="border-t border-(--border) pt-5 space-y-5">
            {osOrder.map((os) => {
              const items = grouped[os];
              if (!items || items.length === 0) return null;

              return (
                <div key={os}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-(--text-muted) mb-3 flex items-center gap-2">
                    <span>{items[0].meta.icon}</span> {os}
                  </p>
                  <div className="space-y-2">
                    {items.map(({ key, asset, meta }) => (
                      <div
                        key={key}
                        className="flex items-center justify-between py-2.5 px-4 rounded-xl transition-all hover:bg-[rgba(15,23,42,0.03)]"
                        style={{ border: "1px solid var(--border)" }}
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-[13px] font-medium text-foreground">
                            {meta.label}
                          </span>
                          <span className="text-[11px] text-(--text-muted) ml-3">
                            {formatSize(asset.size)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 ml-4 shrink-0">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(asset.sha256);
                            }}
                            className="text-[10px] font-mono text-(--text-muted) hover:text-(--text-secondary) transition-colors cursor-pointer"
                            title={`SHA256: ${asset.sha256}`}
                          >
                            SHA256
                          </button>
                          <a
                            href={asset.url}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[12px] font-semibold transition-all"
                            style={{
                              background: "#0F172A",
                              color: "#fff",
                              boxShadow: "0 1px 3px rgba(15,23,42,0.12)",
                            }}
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M12 5v14M5 12l7 7 7-7" />
                            </svg>
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Product Tabs ────────────────────────────────────────────── */

type Product = "ide" | "agent";

function ProductTabs({ product, onSelect }: { product: Product; onSelect: (p: Product) => void }) {
  const tabs: { id: Product; label: string }[] = [
    { id: "ide", label: "IDE" },
    { id: "agent", label: "Code Agent" },
  ];
  return (
    <div
      className="inline-flex items-center gap-1 p-1 rounded-xl mb-8"
      style={{ background: "rgba(15,23,42,0.04)", border: "1px solid var(--border)" }}
      role="tablist"
      aria-label="Product"
    >
      {tabs.map((t) => {
        const active = t.id === product;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(t.id)}
            className="px-5 py-2 rounded-lg text-[13px] font-semibold transition-all"
            style={
              active
                ? { background: "#0F172A", color: "#fff", boxShadow: "0 1px 6px rgba(15,23,42,0.2)" }
                : { color: "var(--text-secondary)" }
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────── */

export default function ReleasesPage() {
  const [product, setProduct] = useState<Product>("ide");
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Loading/error are reset in handleSelectProduct (the event handler), not
  // here — a synchronous setState inside an effect trips
  // react-hooks/set-state-in-effect and causes a cascading render.
  useEffect(() => {
    let cancelled = false;
    fetch(product === "agent" ? "/api/releases?product=agent" : "/api/releases")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch releases");
        return r.json();
      })
      .then((data: ReleasesResponse) => {
        if (cancelled) return;
        setReleases(data.releases);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [product]);

  const handleSelectProduct = (p: Product) => {
    if (p === product) return;
    setProduct(p);
    setLoading(true);
    setError(null);
  };

  return (
    <div style={{ background: "var(--bg-root)", color: "var(--text-primary)" }}>

      <section className="pt-28 pb-8 px-6">
        <div className="max-w-225 mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-8 text-[12px] text-(--text-muted)">
            <Link href="/download" className="hover:text-(--text-secondary) transition-colors">
              Download
            </Link>
            <span>/</span>
            <span className="text-(--text-secondary) font-medium">Previous Releases</span>
          </div>

          {/* Title */}
          <h1 className="text-[clamp(2rem,5vw,3rem)] font-black tracking-tight leading-[1.05] mb-3">
            <span className="gradient-text">Previous Releases</span>
          </h1>
          <p className="text-[15px] text-(--text-secondary) max-w-lg mb-8">
            All stable builds of the MonkeysCode IDE and Code Agent, sorted by version. Each release includes SHA-256 verified downloads for all platforms.
          </p>

          {/* Product switcher */}
          <ProductTabs product={product} onSelect={handleSelectProduct} />
        </div>
      </section>

      <div className="gradient-line max-w-4xl mx-auto" />

      <section className="py-12 px-6 flex-1">
        <div className="max-w-225 mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div
                className="inline-block w-6 h-6 rounded-full border-2 border-t-transparent animate-spin mb-4"
                style={{ borderColor: "var(--border)", borderTopColor: "transparent" }}
              />
              <p className="text-[13px] text-(--text-muted)">Loading releases...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-[15px] text-(--text-muted) mb-4">
                No releases available yet.
              </p>
              <p className="text-[13px] text-(--text-secondary)">
                The first build is currently running. Check back soon!
              </p>
              <Link
                href="/download"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl text-[13px] font-semibold transition-all"
                style={{
                  background: "#0F172A",
                  color: "#fff",
                  boxShadow: "0 1px 6px rgba(15,23,42,0.15)",
                }}
              >
                ← Back to Download
              </Link>
            </div>
          ) : releases.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[15px] text-(--text-muted)">
                No releases published yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {releases.map((release, i) => (
                <ReleaseCard key={`${product}-${release.version}`} release={release} isLatest={i === 0} />
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="gradient-line max-w-4xl mx-auto" />

      <section className="py-16 px-6 text-center">
        <Link
          href="/download"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[13px] font-semibold transition-all"
          style={{
            background: "#0F172A",
            color: "#fff",
            boxShadow: "0 1px 6px rgba(15,23,42,0.15)",
          }}
        >
          ← Back to Download
        </Link>
      </section>

    </div>
  );
}
